import React, { RefObject, useEffect, useRef, useState } from "react";
import { mdiRefresh } from "@mdi/js";
import Icon from "@mdi/react";

import { LoadingSpinnerSVG } from "components/SVGIcons";
import { usePullToRefreshHost } from "context/PullToRefreshContext";
import { attachPullToRefreshGesture, TRIGGER } from "helpers/pullToRefreshGesture";
import styles from "./style.module.scss";

type Phase = "idle" | "pulling" | "done";

export default function PullToRefreshIndicator({
  scrollRef
}: Readonly<{ scrollRef: RefObject<HTMLDivElement> }>) {
  const { isEnabled, isRefreshing, refresh, getSnapshot } = usePullToRefreshHost();
  const [phase, setPhase] = useState<Phase>("idle");
  const circleRef = useRef<HTMLDivElement>(null);
  const state = isRefreshing ? "refreshing" : phase;

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !isEnabled) return;
    let frame = 0;
    let latest = { offset: 0, armed: false };
    const paint = () => {
      frame = 0;
      const circle = circleRef.current;
      if (!circle) return;
      circle.style.setProperty("--ptr-y", `${latest.offset}px`);
      circle.style.setProperty("--ptr-rotate", `${(latest.offset / TRIGGER) * 270}deg`);
      circle.style.setProperty("--ptr-opacity", String(Math.min(1, latest.offset / 32)));
      circle.dataset.armed = String(latest.armed);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };
    const detach = attachPullToRefreshGesture(root, {
      canStart: () => !getSnapshot().isRefreshing,
      onStart: () => {
        latest = { offset: 0, armed: false };
        paint();
        setPhase("pulling");
      },
      onMove: (offset, armed) => {
        latest = { offset, armed };
        if (!frame) frame = requestAnimationFrame(paint);
      },
      onEnd: (armed) => {
        stop();
        if (armed) refresh();
        setPhase(armed ? "done" : "idle");
      },
      onCancel: () => {
        stop();
        setPhase("idle");
      }
    });
    return () => {
      stop();
      detach();
    };
  }, [scrollRef, isEnabled, refresh, getSnapshot]);

  useEffect(() => {
    if (state !== "done") return;
    const timer = window.setTimeout(() => setPhase("idle"), 200);
    return () => window.clearTimeout(timer);
  }, [state]);

  if (!isEnabled && !isRefreshing && phase === "idle") return null;

  return (
    <div className={styles.anchor}>
      <div className={styles.clip} aria-hidden="true">
        <div ref={circleRef} className={styles.circle} data-state={state}>
          {state === "refreshing" || state === "done" ? (
            <LoadingSpinnerSVG className={styles.spinner} />
          ) : (
            <Icon path={mdiRefresh} className={styles.arrow} />
          )}
        </div>
      </div>
      <span className="sr-only" role="status">
        {isRefreshing ? "Refreshing" : ""}
      </span>
    </div>
  );
}
